import type { ReactNode } from "react";
import { Link } from "../../lib/linkComponent";
import { cn } from "../../lib/cn";
import styles from "./TerminalCard.module.css";

/** Root elements a TerminalCard can render as. */
export type TerminalCardElement = "div" | "li" | "article";

/** Heading levels the title can take. */
export type TerminalCardHeadingLevel = 2 | 3 | 4 | 5 | 6;

export interface TerminalCardProps {
  /** Card title, set in the terminal text style on the primary header band */
  title: string;
  /** Short detail at the end of the header band, such as a duration */
  meta?: string;
  /**
   * Destination. The title becomes a link and its hit area stretches over the
   * whole card; the title stays the link's accessible name.
   */
  href?: string;
  /**
   * Renders the title as a heading of this level. Without it the title is
   * plain text, for cards inside a section whose own heading names them.
   */
  headingLevel?: TerminalCardHeadingLevel;
  /** Root element: li inside a list, article for a standalone item. Defaults to `div`. */
  as?: TerminalCardElement;
  /** Body content under the header band */
  children?: ReactNode;
  /** Extra class names on the root */
  className?: string;
}

/**
 * Keeps hyphenated words on one line ("Lift-Off" never ends a line on "Lift-"):
 * the large terminal title wraps on narrow cards, and a lone half word reads
 * badly. The text itself is unchanged for copying and screen readers.
 */
function keepHyphenatedWords(text: string): ReactNode {
  const parts = text.split(/(\S+-\S+)/);
  if (parts.length === 1) return text;
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className={styles.noBreak}>
        {part}
      </span>
    ) : (
      part
    ),
  );
}

/**
 * Card with a primary header band: the title and an optional meta detail in
 * the terminal text style (LisaTerminal Paper), the body below in a framed light-gray
 * panel.
 */
export function TerminalCard({
  title,
  meta,
  href,
  headingLevel,
  as: Root = "div",
  children,
  className,
}: TerminalCardProps) {
  const TitleTag = headingLevel ? (`h${headingLevel}` as const) : "span";

  return (
    <Root className={cn(styles.root, className)}>
      <div className={styles.header}>
        <TitleTag className={styles.title}>
          {href ? (
            // Stretched link: the title is the accessible name and the
            // ::after overlay makes the whole card the hit area.
            <Link href={href} className={styles.link}>
              {keepHyphenatedWords(title)}
            </Link>
          ) : (
            keepHyphenatedWords(title)
          )}
        </TitleTag>
        {meta ? <span className={styles.meta}>{meta}</span> : null}
      </div>
      {children != null ? <div className={styles.body}>{children}</div> : null}
    </Root>
  );
}

TerminalCard.displayName = "TerminalCard";

export default TerminalCard;
